/**
 * Installing a picked Preview version.
 *
 * Rust keeps the pick and points the updater at that version's own feed,
 * accepting it older or newer (`release_channel_preview_version.rs`), so the
 * update fetched here is exactly that build and automatic updates stay on
 * it. "Latest" (null) clears the pick and follows the newest Preview again.
 */
import { invoke } from "@tauri-apps/api/core";
import { translate } from "@/i18n";
import { releaseChannelMessages } from "@/i18n/messages/release-channel";
import { useNotificationStore } from "@/stores/notification-store";
import { installDesktopUpdate, type Update } from "./use-auto-update";

const FAILURE_ID = "preview-version-install-failed";

export interface PreviewVersion {
  version: string;
  pub_date: string;
  title: string;
}

export interface PreviewVersions {
  /** Newest first, none older than Production's live version. */
  versions: PreviewVersion[];
  pinned: string | null;
}

export function loadPreviewVersions(): Promise<PreviewVersions> {
  return invoke<PreviewVersions>("preview_versions");
}

/**
 * Keep `version` (null: the latest) and install it, restarting Denker.
 * Returns when nothing restarted: this build already is that version, or
 * the install failed and said so in a toast.
 */
export async function installPreviewVersion(
  version: string | null,
): Promise<void> {
  try {
    await invoke("preview_version_pin", { version });
    const update = await invoke<Update | null>("desktop_update_fetch");
    if (update) await installDesktopUpdate(update);
  } catch (error) {
    useNotificationStore.getState().addSystemToast(() => ({
      id: FAILURE_ID,
      appWide: true,
      level: "error",
      title: translate(releaseChannelMessages, "installFailed"),
      description: error instanceof Error ? error.message : String(error),
    }));
  }
}
